import axios from 'axios';

// Create an instance of axios
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// ==================================================================
// 🎭 DEMO MODE: SENSITIVE DATA SCRUBBER
// ==================================================================
// Reads dynamically from your browser so you can toggle it via the SuperAdmin UI
const isDemoMode = () => localStorage.getItem('demoMode') === 'true';

const SENSITIVE_SBIS = ['106439685', '106801732', '106395605', '108476709']; // 👈 Your real SBIs

// Create a reverse-dictionary so the frontend can still talk to the backend
const REVERSE_SBI_MAP = {};
SENSITIVE_SBIS.forEach(sbi => {
    REVERSE_SBI_MAP[`DEMO-${sbi.slice(-3)}`] = sbi;
});

// The Interceptor's Memory: Keeps fake field names perfectly consistent
const fieldMap = new Map();
let fieldCounter = 1;

const getFakeFieldName = (realRef) => {
    if (!realRef) return realRef;
    if (!fieldMap.has(realRef)) {
        const paddedNum = String(fieldCounter).padStart(3, '0');
        fieldMap.set(realRef, `Demo Field ${paddedNum}`);
        fieldCounter++;
    }
    return fieldMap.get(realRef);
};

// 🛡️ Protect GIS attributes so the map renders correctly
const GIS_KEYS = new Set(['geometry', 'coordinates', 'bbox', 'extent', 'spatialReference', 'rings', 'paths', 'x', 'y', 'z', 'features']);

// Recursive function to scrub the data coming IN
const maskData = (obj, isSensitive = false) => {
    // Safely ignore nulls and binary data (like map vector tiles)
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date || obj instanceof Blob || obj instanceof ArrayBuffer) return obj;

    if (Array.isArray(obj)) {
        // Skip coordinate arrays entirely to save CPU
        if (obj.length > 0 && typeof obj[0] === 'number') return obj;
        return obj.map(item => maskData(item, isSensitive));
    } 
    
    const newObj = { ...obj };
    let currentlySensitive = isSensitive;
    
    // Convert to string safely in case the backend sent an integer
    const objSbi = newObj.sbi ? String(newObj.sbi) : null;
    
    if (objSbi && SENSITIVE_SBIS.includes(objSbi)) {
        currentlySensitive = true; 
        
        const shortSbi = objSbi.slice(-3);
        newObj.sbi = `DEMO-${shortSbi}`; 
        
        if (newObj.userName) newObj.userName = `Demo Farmer ${shortSbi}`;
        if (newObj.user && newObj.user.name) newObj.user.name = `Demo Farmer ${shortSbi}`;
        if (newObj.email) newObj.email = `demo${shortSbi}@example.com`;
    }

    if (currentlySensitive) {
        if (newObj.parcelRef) newObj.parcelRef = getFakeFieldName(newObj.parcelRef);
        if (newObj.customName) newObj.customName = getFakeFieldName(newObj.customName);
        if (newObj.parcelId) newObj.parcelId = `fake-id-${String(newObj.parcelId).slice(-6)}`;
    }

    for (const key in newObj) {
        // 🛡️ Skip all mapping/GIS keys to prevent destroying ArcGIS data
        if (GIS_KEYS.has(key)) continue; 
        
        newObj[key] = maskData(newObj[key], currentlySensitive);
    }
    
    return newObj;
};
// ==================================================================


// 👇 THE REQUEST INTERCEPTOR (Outgoing to Server)
api.interceptors.request.use(
  (config) => {
    // 1. Check if this is an external API call (like ArcGIS servers)
    const isExternal = config.url && config.url.startsWith('http') && !config.url.startsWith(config.baseURL);

    // 2. Authentication (Only send our token to our own backend)
    const token = localStorage.getItem('token');
    if (token && !isExternal) {
      config.headers['x-auth-token'] = token;
    }

    // 2b. Impersonation Mode (SuperAdmin tenant override)
    const impersonatedClientId = localStorage.getItem('impersonatedClientId');
    if (impersonatedClientId && !isExternal) {
      config.headers['x-selected-client-id'] = impersonatedClientId;
    }

    // 3. DEMO MODE REVERSE TRANSLATION (Only for internal API)
    if (isDemoMode() && !isExternal) {
        let urlString = config.url || '';
        Object.keys(REVERSE_SBI_MAP).forEach(demoSbi => {
            if (urlString.includes(demoSbi)) {
                urlString = urlString.replace(demoSbi, REVERSE_SBI_MAP[demoSbi]);
            }
        });
        config.url = urlString;

        if (config.data && typeof config.data === 'object') {
            const dataSbiStr = config.data.sbi ? String(config.data.sbi) : null;
            if (dataSbiStr && REVERSE_SBI_MAP[dataSbiStr]) {
                config.data.sbi = REVERSE_SBI_MAP[dataSbiStr];
            }
        }
    }

    return config;
  },
  (error) => Promise.reject(error)
);


// 👇 THE RESPONSE INTERCEPTOR (Incoming from Server)
api.interceptors.response.use(
  (response) => {
    const isExternal = response.config.url && response.config.url.startsWith('http') && !response.config.url.startsWith(response.config.baseURL);

    // 1. DEMO MODE MASKING (Skip external APIs entirely)
    if (isDemoMode() && response.data && !isExternal) {
        try {
            response.data = maskData(response.data);
        } catch (err) {
            console.error("Demo Mode masking failed, bypassing to prevent crash:", err);
        }
    }
    return response;
  },
  (error) => {
    // 2. ACCOUNT SUSPENSION CHECK
    if (error.response && error.response.status === 403 && error.response.data.msg === 'ACCOUNT_SUSPENDED') {
      localStorage.removeItem('token');
      localStorage.removeItem('deviceToken');
      window.location.href = '/account-suspended';
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export default api;